<template>
  <div v-if="product">
    <div class="product-item-form-option">
      <h3 class="product-item-form-option__title">
        <div
          v-if="product.image"
          class="product-item-form-option__img"
        >
          <img :src="product.image" alt="img" />
        </div>
        {{ product.name }}
      </h3>

      <!-- Standard product -->
      <template v-if="isStandard">

        <!-- Per Unit Price -->
        <div class="product-item-form-option__amount">
          <span>Charge amount ($)</span>
          <span>{{ product.price.amount }}</span>
        </div>

        <!-- Quantity -->
        <div class="product-item-form-option__quantity">
          <label>Quantity</label>
          <span v-if="isDisabledToEdit">x {{ quantity }}</span>
          <div v-else class="quantity-control">
            <button
              type="button"
              @click="decrease"
            >-</button>
            <input
              type="number"
              min="1"
              v-model.number="quantity"
              @keydown="preventNegative"
            />
            <button
              type="button"
              @click="increase"
            >+</button>
          </div>
        </div>
      </template>

      <!-- Donation -->
      <template v-if="isDonation">
        <div class="product-item-form-option__donation">
          <label>Donation amount ($)</label>

          <input
            type="number"
            min="1"
            step="0.01"
            v-model.number="donationAmount"
            :disabled="isDisabledToEdit"
            placeholder="Enter amount"
            class="donation-input"
            @keydown="preventNegative"
          />
        </div>
      </template>

      <!-- Fee line items — calculated against this product's own subtotal -->
      <template v-if="applicableFees.length">
        <div
          v-for="fee in applicableFees"
          :key="'product_fee_' + fee.id"
          class="product-item-form-option__fee"
        >
          <span>
            {{ fee.name }}
            <span class="product-item-form-option__fee-hint">
              ({{ fee.type === 'percentage' ? fee.value + '%' : '$' + fee.value }})
            </span>
          </span>
          <span>+${{ fee.calculatedAmount.toFixed(2) }}</span>
        </div>
      </template>

      <!-- Total (subtotal + fees) -->
      <div class="product-item-form-option__total">
        <span>Total:</span>
        <strong>${{ totalWithFees.toFixed(2) }}</strong>
      </div>

      <!-- Description -->
      <div
        class="product-item-form-option__description"
        v-html="product.description"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { calculateApplicableFees } from '@/utils/payment'

export default {
  props: {
    isDisabledField: {
      type: Boolean,
      default: false,
    },
    productFieldData: {
      type: Object,
      default: () => ({}),
    },
    productUuid: {
      type: String,
    },
    stepStatus: {
      type: String,
      default: '',
    },
    // Raw fee configs from the program — amounts computed locally
    paymentFees: {
      type: Array,
      default: () => [],
    },
    // The active payment method string ('Credit Card', 'ACH', etc.)
    paymentMethod: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      product: null,
      quantity: 1,
      donationAmount: null,
    }
  },
  watch: {
    quantity(val) {
      if (this.isDonation) return

      this.$set(this.productFieldData, 'quantity', val)
      this.$emit('quantity-changed')
    },
    donationAmount(val) {
      if (!this.isDonation) return

      let safeValue = Number(val)

      if (Number.isNaN(safeValue) || safeValue <= 0) {
        safeValue = 1
      }

      if (safeValue !== val) {
        this.donationAmount = safeValue
        return
      }

      this.$set(this.productFieldData, 'amount', safeValue)
      this.$emit('donation-amount-changed')
    },
  },
  async mounted() {
    await this.initPreparedValues()
    await this.prepareProductSettings()
  },
  computed: {
    ...mapGetters({
      programId: 'applicationRegistration/getProgramID',
      productList: 'applicationRegistration/getProductsList',
    }),
    isDonation() {
      return this.product?.type === 'donation'
    },
    isStandard() {
      return !this.isDonation
    },
    /** Base subtotal for this product (price × qty, or donation amount) */
    subtotal() {
      if (!this.product || !this.product.price) return 0
      if (this.isDonation) return Number(this.donationAmount || 0)
      return this.product.price.amount * (this.quantity || 1)
    },
    /**
     * Fees applicable to this product's own subtotal.
     * Recomputes automatically when quantity or donationAmount changes
     * because subtotal is reactive to those data properties.
     */
    applicableFees() {
      if (!this.paymentMethod || !this.subtotal) return []
      return calculateApplicableFees(this.paymentFees, this.paymentMethod, this.subtotal, 'Product')
    },
    totalFeeAmount() {
      return this.applicableFees.reduce((sum, f) => sum + f.calculatedAmount, 0)
    },
    totalWithFees() {
      return this.subtotal + this.totalFeeAmount
    },
    isDisabledToEdit() {
      return ['Approved', 'In Review'].includes(this.stepStatus) || this.isDisabledField
    },
  },
  methods: {
    preventNegative(e) {
      if (e.key === '-' || e.key === 'e') {
        e.preventDefault()
      }
    },
    async initPreparedValues() {
      if (this.isDonation) {
        this.donationAmount =
          this.productFieldData?.value?.amount ??
          this.productFieldData?.amount ??
          null
      } else {
        this.quantity =
          this.productFieldData?.value?.quantity ??
          this.productFieldData?.quantity ??
          1
      }
    },
    increase() {
      this.quantity++
    },
    decrease() {
      if (this.quantity > 1) this.quantity--
    },
    async prepareProductSettings() {
      this.product = this.productList.find(
        product => product.uuid === this.productUuid
      )

      if (!this.product) return

      if (this.product.price) {
        this.productFieldData.amount = this.product.price.amount
      }

      this.productFieldData.product_name = this.product.name
    },
  },
}
</script>

<style scoped>
.product-item-form-option__fee {
  display: flex;
  justify-content: space-between;
  font-size: 0.88em;
  color: #666;
  margin-top: 4px;
  margin-bottom: 2px;
}

.product-item-form-option__fee-hint {
  font-size: 0.82em;
  color: #999;
  margin-left: 3px;
}
</style>
